export type TestimonialCategory = 'companies' | 'schools' | 'users'

export type Testimonial = {
  id: string
  category: TestimonialCategory
  name: string
  role?: string
  linkedinUrl?: string
  summary: string
  paragraphs: string[]
  /** True when the full content fits in a single-height card without doubling */
  isShort?: boolean
}

export const TESTIMONIAL_CATEGORIES: TestimonialCategory[] = [
  'companies',
  'schools',
  'users',
]

export const TESTIMONIALS: Testimonial[] = [
  {
    id: 'gil-luz',
    category: 'companies',
    name: 'Gil Luz',
    role: 'Founder at Data Guild',
    linkedinUrl: 'https://www.linkedin.com/in/gil-luz-11279522/',
    summary:
      "We only hire top data talent — XP-Lab transformed how we evaluate candidates. Less stress, better performance, a much clearer picture of true capabilities.",
    paragraphs: [
      "At Data Guild, we only hire top data talent, which means our assessment process needs to match that standard. XP-Lab transformed how we evaluate candidates. Instead of the usual high-pressure, unfamiliar testing environments, our candidates get a familiar IDE-like experience where they can actually do what they do best. The result? Less stress, better performance, and a much clearer picture of each candidate's true capabilities. It's a win for them and a win for us.",
    ],
  },
  {
    id: 'hannan-kravitz',
    category: 'companies',
    name: 'Hannan Kravitz',
    role: 'Head of Data Engineering and Science at Rounds',
    linkedinUrl: 'https://www.linkedin.com/in/hannan-kravitz/',
    summary:
      'XP-Lab moved us beyond theoretical interviews — candidates demonstrate expertise in action, with the platform aligning to my exact criteria.',
    paragraphs: [
      'In the current AI and LLM era, identifying top-tier data engineering talent has become increasingly challenging. Upon stepping into my role as Head of Data Engineering and opening a new position within the group, I began searching for a more robust way to vet candidates.',
      'I was introduced to XP-Lab through a colleague, and after a brief consultation with Tal, it became clear that this platform was the solution we needed. XP-Lab allows us to move beyond theoretical interviews by enabling candidates to demonstrate their expertise in action. Most impressively, the platform seamlessly aligned with my specific criteria, evaluating multiple technical factors throughout the assessment process to ensure a comprehensive evaluation.',
    ],
  },
  {
    id: 'or-blan',
    category: 'companies',
    name: 'Or Blan',
    role: 'Managing Partner at Creative Geek',
    linkedinUrl: 'https://www.linkedin.com/in/orblan/',
    summary:
      "XP-Lab cuts through the noise — most candidates produce polished answers, but that doesn't mean they can operate in a real analytical environment.",
    paragraphs: [
      'Working with XP-Lab changed how we evaluate data professionals — and frankly, exposed how limited most hiring processes still are.',
      "At Creative Geek, we help companies build and run better hiring processes. The recurring issue is clear: most candidates can produce polished answers, but that doesn't mean they can actually operate in a real analytical environment.",
      "XP-Lab cuts through that noise. Instead of relying on CVs, interviews, or overly clean take-home tasks, it puts candidates into realistic scenarios where ambiguity, messy data, and business context are part of the problem. That's where the real signal shows up.",
      'For us, this has significantly improved our ability to evaluate candidates on behalf of our clients — and more importantly, to confidently recommend the ones who can actually do the job, not just present well.',
    ],
  },
  {
    id: 'sivan-milwerger',
    category: 'companies',
    name: 'Sivan Milwerger',
    role: 'Head of Data at iRestore',
    linkedinUrl: 'https://www.linkedin.com/in/sivan-milwerger/',
    summary:
      'Strong product intuition and execution speed. They simplify complexity without losing precision — rarely seen this well.',
    paragraphs: [
      'The founders and management team at XP-Lab stand out for their ability to deeply understand complex workflows and translate them into simple, practical solutions.',
      "In my experience working with data and analytics systems, extracting insights often requires strong technical skills (SQL, BI tools, etc.), which creates a significant barrier for many users. What impressed me about XP-Lab's system is how effectively it removes that barrier — enabling non-technical users to access insights in a clear, intuitive, and actionable way, without compromising on depth or accuracy.",
      "This product clarity is a direct reflection of the team behind it. They demonstrate strong product intuition, move quickly, and are highly attentive to real user needs. Their ability to simplify complexity without losing precision is something I've rarely seen done this well.",
      "Compared to other founding teams I've worked with, XP-Lab stands out in their combination of execution speed, user-centric thinking, and practical problem-solving.",
      'If I were evaluating XP-Lab from an investment perspective, both the strength of the team and the clarity of the product would give me strong confidence in their ability to build something meaningful and scalable.',
    ],
  },
  {
    id: 'liraz-golan',
    category: 'companies',
    name: 'Liraz Golan',
    role: 'Tech Lead & South PM at Place IL',
    linkedinUrl: 'https://www.linkedin.com/in/liraz-golan/',
    summary:
      'A strategic partner with a system perfectly calibrated for what the AI era demands — taking screening five steps forward.',
    paragraphs: [
      'Place-IL is a social venture dedicated to creating a "highway" for underrepresented talent into Israel\'s tech industry, ensuring that the next generation of high-tech professionals is as diverse as it is skilled.',
      'Our collaboration with Tal began when we were searching for a volunteer to help refine our data process. What we found in Tal and his system at XP-Lab was far beyond a simple volunteer effort; we gained a strategic partner with a system that is perfectly calibrated for what the market demands in the AI era.',
      "To find the \"future junior\" in data analysis today, it isn't enough to just collect and organize data. XP-Lab's approach takes the screening process five steps forward, identifying true potential through a modern, tech-forward lens.",
      "Working with XP-Lab offers several distinct advantages: extreme adaptability — the system grasped the rules of the game at Place-IL almost instantly, adjusting to our specific requirements without missing a beat. Proactive focus — Tal didn't just provide a tool; he actively cared about the success of the process and the future of the candidates. And professional depth — a pro touch derived from years of experience, allowing him to anticipate needs before they arise.",
      "XP-Lab's system provides a sophisticated, modern approach to data that goes well beyond standard management. I highly recommend it for any organization looking to elevate their technical screening and data processes.",
    ],
  },
  {
    id: 'menachem-gerstman',
    category: 'schools',
    name: 'Menachem Gerstman',
    role: 'Lead Teacher at Experis',
    linkedinUrl: 'https://www.linkedin.com/in/menachem-gerstman-a4a29147/',
    summary:
      'XP-Lab let me focus on what actually matters — bridging the gap to true business thinking, while delivering personalized feedback at scale.',
    paragraphs: [
      'Mentoring a course for aspiring data analysts presents a challenge: while students today navigate technology and coding more easily than ever, bridging the gap to true business thinking remains difficult. Managing individual progress in a group and teaching how to do data preparation, best practices, give actionable insights and conclusions, while also providing personalized feedback to every student is an incredibly time-consuming task.',
      "Working with XP-Lab allowed me to shift my focus to what actually matters. The platform helped me identify cross-sectional challenges, enabling me to address common pain points in group sessions while simultaneously pinpointing the specific areas where each student needed individual growth. This ensured they developed the analytical mindset required for today's job market.",
      "Furthermore, the XP-Lab team curated tailored business scenarios that aligned perfectly with the students' interests and the industries they aim to enter. As an analyst and former educator, the efficiency and focus I gained through XP-Lab were invaluable. It saved me significant time, allowing me to dedicate my energy to my students' core development — sharpening their business acumen and equipping them with the practical tools they need to succeed in the real world.",
    ],
  },
  {
    id: 'asaf-elkan',
    category: 'schools',
    name: 'Asaf Elkan',
    role: 'CEO of G-Academy & Business Development Manager, G-STAT',
    linkedinUrl: 'https://www.linkedin.com/in/asaf-elkan-59a47752/',
    summary:
      "By far the best automated tool I've used for sourcing and screening — combining automation with deep business and analytical understanding.",
    paragraphs: [
      "This is by far the best automated tool I've used for sourcing and screening data professionals. It significantly improves the efficiency and quality of the hiring process by enabling precise filtering and fast identification of relevant candidates.",
      'What really stands out is the ability to combine automation with strong relevance, together with a deep business and analytical understanding — not just finding candidates, but finding the right ones for us.',
    ],
  },
  {
    id: 'amir-os',
    category: 'users',
    name: 'Amir Os',
    linkedinUrl: 'https://www.linkedin.com/in/amir-os/',
    summary:
      "Genuinely different from anything else I've done before. The challenges have real value, and the feedback helps you think sharper.",
    paragraphs: [
      "I'm really enjoying XP-Lab! Mainly because it feels genuinely different from anything else I've done before. And I've done quite a bit: four courses and training programs at well-known places.",
      "What I appreciate most are the challenges themselves. Even on their own, they have real value that's hard to find elsewhere.",
      'And the feedback takes it a step further and actually helps you think sharper and get better.',
      'A seriously great platform.',
    ],
  },
  {
    id: 'danielle-menuhin',
    category: 'users',
    name: 'Danielle Menuhin',
    linkedinUrl: 'https://www.linkedin.com/in/danielle-menuhin/',
    isShort: true,
    summary:
      'Real analytical feedback without sugarcoating — exactly what I needed for interviews.',
    paragraphs: [
      "Hi XP-Lab! Have to say, I was skeptical at first — I definitely don't need another place to practice SQL. But after doing one challenge I want everyone to experience this!",
      'Real analytical feedback without sugarcoating is what a person really needs to pass interviews, and finally I can get that without bugging other people. Wonderful platform — thank you!!',
    ],
  },
  {
    id: 'shahar-gomel',
    category: 'users',
    name: 'Shahar Gomel',
    linkedinUrl: 'https://www.linkedin.com/in/shahar-gomel/',
    isShort: true,
    summary:
      "I haven't found another place that gives such high-quality, practical, and fast feedback for in-depth analysis.",
    paragraphs: [
      "The system helped me improve my analytical skills and made an impact on my day-to-day work. I haven't found another place that gives such high-quality, practical, and fast feedback for in-depth analysis like XP-Lab. Highly recommended!",
    ],
  },
]
